// FormWriteService.js

/**
 * FormWriteService.js
 *
 * Service for writing form template documents to Firestore.
 */

import { deleteDoc, doc, setDoc, Timestamp, updateDoc } from 'firebase/firestore';
import { collections, db } from 'firebaseConfig';

import {
  buildDefaultDetailsFormPayload,
  buildDefaultFormLayoutScaffold,
  buildFormDocId,
  buildFormPayload,
  getVisibleFormFieldKeys,
} from 'services/forms/FormModel';
import { ensureLayoutHasAssignedBlock } from 'services/layouts/LayoutModel';
import { resolveBaseKey, withBaseKey } from 'utilities/basePersistence';

const FORMS_COLLECTION = collections.forms;

function normalizeString(value) {
  return typeof value === 'string' ? value.trim() : '';
}

export async function createForm({
  key,
  baseKey,
  typeKey,
  userKey,
  title,
  description,
  position,
  isDefault,
  layoutEdited,
  layout,
  blocksByKey,
} = {}) {
  const payload = buildFormPayload({
    blocksByKey,
    description,
    isDefault,
    key,
    layout,
    layoutEdited,
    position,
    title,
    typeKey,
    userKey,
    baseKey,
  });

  if (!payload) {
    return null;
  }

  const docId = buildFormDocId({
    key: payload.key,
    typeKey: payload.typeKey,
    baseKey: resolveBaseKey(payload),
  });

  await setDoc(doc(db, FORMS_COLLECTION, docId), payload, { merge: false });
  return payload;
}

export async function createDefaultDetailsForm({
  baseKey,
  typeKey,
  userKey,
  typeDef = null,
} = {}) {
  const payload = buildDefaultDetailsFormPayload({
    typeDef,
    typeKey,
    userKey,
    baseKey,
  });

  if (!payload) {
    return null;
  }

  const docId = buildFormDocId({
    key: payload.key,
    typeKey: payload.typeKey,
    baseKey: resolveBaseKey(payload),
  });

  await setDoc(doc(db, FORMS_COLLECTION, docId), payload, { merge: false });
  return payload;
}

export async function updateForm(formKey, data = {}, { userKey = '', baseKey = '', typeKey = '' } = {}) {
  const cleanedFormKey = normalizeString(formKey);
  if (!cleanedFormKey || !data || typeof data !== 'object') {
    return;
  }

  const resolvedBaseKey = normalizeString(
    baseKey || resolveBaseKey(data),
  );
  const docId = buildFormDocId({
    key: cleanedFormKey,
    typeKey: normalizeString(typeKey || data.typeKey),
    baseKey: resolvedBaseKey,
  });

  const shouldNormalizeLayoutScaffold = Object.prototype.hasOwnProperty.call(data, 'layout')
    || Object.prototype.hasOwnProperty.call(data, 'blocksByKey');
  const normalizedLayoutScaffold = shouldNormalizeLayoutScaffold
    ? ensureLayoutHasAssignedBlock({
        blocksByKey: data.blocksByKey,
        layout: data.layout,
      })
    : null;

  const payload = withBaseKey({
    ...data,
    ...(normalizedLayoutScaffold
      ? {
          blocksByKey: normalizedLayoutScaffold.blocksByKey,
          layout: normalizedLayoutScaffold.layout,
        }
      : {}),
    dateModified: Timestamp.now(),
    ...(normalizeString(userKey) ? { updatedBy: normalizeString(userKey) } : {}),
  }, resolvedBaseKey);

  const hasExplicitLayoutEdited = Object.prototype.hasOwnProperty.call(data, 'layoutEdited');
  if (payload.isDefault && normalizedLayoutScaffold && !hasExplicitLayoutEdited) {
    payload.layoutEdited = true;
  }

  await updateDoc(doc(db, FORMS_COLLECTION, docId), payload);
}

export async function deleteFormByKey(formKey, { baseKey = '', typeKey = '' } = {}) {
  const cleanedFormKey = normalizeString(formKey);
  const cleanedBaseKey = normalizeString(baseKey);
  const cleanedTypeKey = normalizeString(typeKey);

  if (!cleanedFormKey || !cleanedBaseKey || !cleanedTypeKey) {
    return;
  }

  const docId = buildFormDocId({
    key: cleanedFormKey,
    typeKey: cleanedTypeKey,
    baseKey: cleanedBaseKey,
  });

  await deleteDoc(doc(db, FORMS_COLLECTION, docId));
}

export async function refreshDefaultFormBootstrap(formDoc, { typeDef = null, userKey = '' } = {}) {
  if (!formDoc?.isDefault || formDoc?.layoutEdited) {
    return formDoc;
  }

  const fieldKeys = getVisibleFormFieldKeys(typeDef);
  const { blocksByKey, layout } = buildDefaultFormLayoutScaffold({ fieldKeys });

  await updateForm(formDoc.key, {
    blocksByKey,
    layout,
    layoutEdited: false,
    typeKey: formDoc.typeKey,
  }, {
    typeKey: formDoc.typeKey,
    userKey,
    baseKey: resolveBaseKey(formDoc),
  });

  return {
    ...formDoc,
    blocksByKey,
    layout,
    layoutEdited: false,
  };
}

export async function saveFormDocument(formDoc, { userKey = '' } = {}) {
  const cleanedFormKey = normalizeString(formDoc?.key);
  if (!cleanedFormKey) return null;

  const { blocksByKey, layout } = ensureLayoutHasAssignedBlock({
    blocksByKey: formDoc?.blocksByKey,
    layout: formDoc?.layout,
  });

  await updateForm(cleanedFormKey, {
    blocksByKey,
    layout,
    layoutEdited: formDoc?.isDefault ? true : Boolean(formDoc?.layoutEdited),
    typeKey: formDoc?.typeKey,
  }, {
    typeKey: formDoc?.typeKey,
    userKey,
    baseKey: resolveBaseKey(formDoc),
  });

  return {
    ...formDoc,
    blocksByKey,
    layout,
    layoutEdited: formDoc?.isDefault ? true : Boolean(formDoc?.layoutEdited),
  };
}
